import { Megaphone, Eye, Target, Loader2, Check, Download, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const CampaignTab = ({
  campaignName, setCampaignName,
  campaignGoal, setCampaignGoal,
  creatingCampaign, campaignResult,
  onCreateCampaign,
  credits, CAMPAIGN_COST
}) => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <div className="lg:col-span-2 space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-xl bg-gradient-to-r from-orange-500 to-pink-600">
            <Megaphone className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">Create Campaign</h2>
            <p className="text-gray-500 text-sm">AI generates a 5-post social media campaign</p>
          </div>
          <div className="ml-auto">
            <span className="px-3 py-1 rounded-full bg-gradient-to-r from-orange-100 to-pink-100 text-orange-700 text-sm font-medium">
              Agency Mode
            </span>
          </div>
        </div>

        <div className="p-4 rounded-xl bg-gradient-to-r from-orange-50 to-pink-50 border border-orange-200 mb-6">
          <p className="text-sm text-orange-800">
            <strong>Full Social Media Marketing Service</strong> - Perfect for busy merchants who want hands-off marketing. 
            AI creates a complete 5-week content plan with images, videos, captions, and hashtags.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-gray-700 dark:text-gray-300 font-medium">Campaign Name</Label>
            <Input
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              placeholder="e.g., Summer Sale 2026"
              className="mt-2 bg-gray-50 dark:bg-gray-700 border-gray-200"
              data-testid="campaign-name-input"
            />
          </div>

          <div>
            <Label className="text-gray-700 dark:text-gray-300 font-medium">Campaign Goal</Label>
            <div className="grid grid-cols-2 gap-4 mt-2">
              {[
                { id: "awareness", Icon: Eye, label: "Brand Awareness", desc: "Grow your audience", color: "orange" },
                { id: "sales", Icon: Target, label: "Drive Sales", desc: "Convert visitors", color: "pink" },
              ].map(({ id, Icon, label, desc, color }) => (
                <button
                  key={id}
                  onClick={() => setCampaignGoal(id)}
                  className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                    campaignGoal === id
                      ? `border-${color}-500 bg-${color}-50`
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  data-testid={`goal-${id}`}
                >
                  <Icon className={`h-5 w-5 ${campaignGoal === id ? `text-${color}-600` : "text-gray-400"}`} />
                  <div className="text-left">
                    <p className={`font-medium ${campaignGoal === id ? `text-${color}-800` : "text-gray-700 dark:text-gray-300"}`}>
                      {label}
                    </p>
                    <p className="text-xs text-gray-500">{desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <Button
            onClick={onCreateCampaign}
            disabled={creatingCampaign || !campaignName.trim() || credits < CAMPAIGN_COST}
            className="w-full py-6 text-lg bg-gradient-to-r from-orange-600 to-pink-600 hover:from-orange-700 hover:to-pink-700 shadow-lg"
            data-testid="create-campaign-btn"
          >
            {creatingCampaign ? (
              <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Creating Your Campaign...</>
            ) : (
              <><Megaphone className="h-5 w-5 mr-2" />Create 5-Post Campaign ({CAMPAIGN_COST} credits)</>
            )}
          </Button>
        </div>
      </div>

      {campaignResult && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-800 dark:text-white flex items-center gap-2">
              <Check className="h-5 w-5 text-green-500" />
              Campaign: {campaignResult.name}
            </h3>
            <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm">5 Posts Ready</span>
          </div>
          <div className="space-y-4">
            {campaignResult.posts?.map((post, idx) => (
              <div key={idx} className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-600">Week {idx + 1}</span>
                  <span className="text-xs text-gray-400">{post.scheduled_date}</span>
                </div>
                <div className="flex gap-4">
                  <div className="w-24 h-24 bg-slate-200 rounded-lg overflow-hidden shrink-0">
                    <img src={post.image_url} alt={post.caption} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800 dark:text-white mb-1">{post.caption}</p>
                    <p className="text-xs text-blue-600 mb-2">{post.hashtags}</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="text-xs">
                        <Download className="h-3 w-3 mr-1" />Download
                      </Button>
                      <Button size="sm" className="text-xs bg-blue-600 hover:bg-blue-700">
                        <Calendar className="h-3 w-3 mr-1" />Schedule
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>

    <div className="space-y-6">
      <div className="bg-gradient-to-br from-orange-600 to-pink-600 rounded-2xl p-6 text-white">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Megaphone className="h-5 w-5" />Agency Mode Benefits
        </h3>
        <ul className="space-y-3 text-sm">
          {["5 Posts Over 5 Weeks", "AI-Generated Images/Videos", "Optimized Captions", "Trending Hashtags", "One-Click Scheduling"].map((item) => (
            <li key={item} className="flex items-center gap-2">
              <Check className="h-4 w-4" />{item}
            </li>
          ))}
        </ul>
        <div className="mt-4 pt-4 border-t border-white/20 text-center">
          <p className="text-2xl font-bold">{CAMPAIGN_COST} credits</p>
          <p className="text-sm text-white/70">per campaign</p>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl">
        <h3 className="font-semibold text-gray-800 dark:text-white mb-4">Campaign Schedule</h3>
        <div className="space-y-3">
          {["Week 1", "Week 2", "Week 3", "Week 4", "Week 5"].map((week, idx) => (
            <div key={idx} className="flex items-center gap-3 p-2 rounded-lg bg-gray-50 dark:bg-gray-700">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 flex items-center justify-center text-white text-sm font-bold">
                {idx + 1}
              </div>
              <span className="text-sm text-gray-600">{week} - 1 Post</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);
